import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import {
  Close,
  Casino,
  TrendingUp,
  Edit,
  ArrowForward,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.88) translateY(10px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;

/**
 * TopicPicker — shown after selecting a subject in practice Learn / Quiz modes.
 *
 * Props:
 *   open               – Boolean dialog visibility
 *   onClose            – Called when user dismisses without choosing
 *   subject            – The subject name just selected
 *   accentColor        – Theme colour (e.g. "#7C3AED" for learn, "#42A5F5" for quiz)
 *   onStart(topic)     – Callback: topic is null for "Surprise Me", string otherwise
 *   lastSessionTopics  – Array of topic strings from the most-recent session (may be empty/null)
 */
const TopicPicker = ({
  open,
  onClose,
  subject,
  accentColor = "#7C3AED",
  onStart,
  lastSessionTopics,
}) => {
  const [customTopic, setCustomTopic] = useState("");
  const [showInput, setShowInput] = useState(false);

  const hasLastTopics = lastSessionTopics && lastSessionTopics.length > 0;

  const handleClose = () => {
    setCustomTopic("");
    setShowInput(false);
    onClose();
  };

  const handlePick = (topic, opts = {}) => {
    setCustomTopic("");
    setShowInput(false);
    onStart(topic, opts);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{ zIndex: 1500 }}
      PaperProps={{
        sx: {
          bgcolor: "#0d1526",
          border: `1.5px solid ${accentColor}44`,
          borderRadius: "24px",
          p: 0,
          maxWidth: 380,
          width: "92vw",
          mx: "auto",
          overflow: "visible",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: "0.6rem",
                color: accentColor,
                letterSpacing: 2.5,
                textTransform: "uppercase",
                mb: 0.5,
              }}
            >
              {subject}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.12rem",
                color: "#fff",
                lineHeight: 1.3,
              }}
            >
              Choose how to pick your topic
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.4)",
              mt: -0.5,
              "&:hover": { color: "rgba(255,255,255,0.7)" },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Option cards */}
        <Box sx={{ px: 3, pb: 3, display: "flex", flexDirection: "column", gap: 1.2 }}>
          {/* Surprise Me */}
          <OptionCard
            icon={<Casino sx={{ fontSize: 22 }} />}
            label="Surprise Me"
            desc="AI picks a topic suited to your level"
            accentColor={accentColor}
            onClick={() => handlePick(null)}
            delay="0s"
          />

          {/* Continue from last session */}
          {hasLastTopics && (
            <OptionCard
              icon={<TrendingUp sx={{ fontSize: 22 }} />}
              label="Continue Last Lesson"
              desc={lastSessionTopics.join(", ")}
              accentColor={accentColor}
              onClick={() => handlePick(lastSessionTopics.join(", "), { continuation: true })}
              delay="0.04s"
            />
          )}

          {/* Specific topic */}
          {!showInput ? (
            <OptionCard
              icon={<Edit sx={{ fontSize: 20 }} />}
              label="Choose a Topic"
              desc="Type exactly what you want to study"
              accentColor={accentColor}
              onClick={() => setShowInput(true)}
              delay={hasLastTopics ? "0.08s" : "0.04s"}
            />
          ) : (
            <Box
              sx={{
                animation: `${popIn} 0.25s ease`,
                border: `1.5px solid ${accentColor}55`,
                borderRadius: "16px",
                p: 2,
                bgcolor: `${accentColor}0a`,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.45)",
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Enter your topic
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  fullWidth
                  autoFocus
                  size="small"
                  value={customTopic}
                  onChange={(e) => setCustomTopic(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && customTopic.trim())
                      handlePick(customTopic.trim());
                  }}
                  placeholder="e.g. Quadratic equations…"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "rgba(255,255,255,0.06)",
                      borderRadius: "12px",
                      color: "#fff",
                      fontSize: "0.88rem",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                      "&.Mui-focused fieldset": { borderColor: accentColor },
                    },
                    "& input::placeholder": {
                      color: "rgba(255,255,255,0.3)",
                      opacity: 1,
                    },
                  }}
                />
                <Button
                  onClick={() => handlePick(customTopic.trim())}
                  disabled={!customTopic.trim()}
                  variant="contained"
                  sx={{
                    minWidth: 44,
                    height: 40,
                    borderRadius: "12px",
                    background: customTopic.trim()
                      ? `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`
                      : "rgba(255,255,255,0.07)",
                    color: customTopic.trim() ? "#fff" : "rgba(255,255,255,0.3)",
                    fontWeight: 800,
                    boxShadow: "none",
                    "&.Mui-disabled": {
                      background: "rgba(255,255,255,0.07)",
                      color: "rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  <ArrowForward fontSize="small" />
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/** Internal option card */
const OptionCard = ({ icon, label, desc, accentColor, onClick, delay = "0s" }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      p: "14px 16px",
      borderRadius: "16px",
      cursor: "pointer",
      border: `1.5px solid rgba(255,255,255,0.08)`,
      bgcolor: "rgba(255,255,255,0.03)",
      animation: `${popIn} 0.3s ${delay} ease both`,
      transition: "all 0.15s ease",
      "&:hover": {
        bgcolor: `${accentColor}14`,
        borderColor: `${accentColor}55`,
        transform: "translateY(-1px)",
      },
      "&:active": { transform: "scale(0.98)" },
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: "12px",
        bgcolor: `${accentColor}18`,
        border: `1px solid ${accentColor}33`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: accentColor,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.92rem",
          color: "#fff",
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.72rem",
          color: "rgba(255,255,255,0.4)",
          lineHeight: 1.3,
          mt: 0.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {desc}
      </Typography>
    </Box>
    <ArrowForward
      sx={{ fontSize: 16, color: "rgba(255,255,255,0.2)", flexShrink: 0 }}
    />
  </Box>
);

export default TopicPicker;
